import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { stringHelper, useClientAppConfigWebApiHandler_getFeature } from '@zg-rentals/rental-manager-env';
import { parseQueryString } from '@zg-rentals/string-utils';
import { Page, PageBreadcrumb, PageContent, Spacer, Tabs, Toast, useToast } from '@zillow/constellation';
import { Box, Paragraph, TextButton } from '@zillow/constellation-10';

import { DashboardContext } from './context';
import { FreemiumContext } from '../../components/Freemium/context';
import { RMWContext } from '../../providers/RMWContext';

import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import freemiumActions from '../../components/Freemium/actions';
import PropertiesActions from '../../actions/PropertiesActions';

import { default as dashboardActions } from './actions';

import accessUtils from '../../utils/accessUtils';

import DashboardCSEvents from '../../model/ClickstreamEvent/DashboardCSEvents';

import { TENANT_PLACEMENT_COOKIES } from '../../constants/CookieConstants';
import PropertyConstants from '../../constants/PropertyConstants';
import ErrorMessageConstants from '../../constants/ErrorMessageConstants';

import { RMW_PATH, RMW_SUB_PATH } from '../../pathsManifest';

import { useGetPropertyDetailsOverview } from '../../hooks/queries/useGetPropertyDetailsOverview';
import { syncCurrentPropertyRestrictions, useGetRestrictions } from '../../hooks/queries/properties';
import { useUpdateSecurityDepositAndRentPrice } from '../../hooks/queries/rentalProtection';

import numberUtils from '../../utils/numberUtils';
import OverviewPage from '../OverviewPage';

import AuthCheckWrapper from '../AuthCheckWrapper';
import ConnectedInterestedRenters from '../../components/ConnectedInterestedRenters';
import ConnectedLeaseAndPropertyConditionModule from '../../components/ConnectedLeaseAndPropertyConditionModule';
import ConnectedListing from '../../components/ConnectedListing';
import ConnectedPaymentsModule from '../../components/ConnectedPaymentsModule';
import FeaturesOfPremium from '../../components/Freemium/components/FeaturesOfPremium';

import SecurityDepositUpdateOnRentGuaranteeDropModal from '../../components/Modal/SecurityDepositUpdateOnRentGuaranteeDropModal';
import SecurityDepositUpdateFailureModal from '../../components/Modal/SecurityDepositUpdateFailureModal';
import ReviewSecurityDepositModal from '../../components/ConnectedPropertyCard/components/ReviewSecurityDepositModal';
import InviteToApplyModal from '../../components/Modal/InviteToApplyModal';

import PropertyCard from './components/PropertyCard';
import Navigation from './components/Navigation';

import RentGuaranteeBannerMultiPolicy from '../../components/ConnectedPropertyCard/components/RentGuaranteeBannerMultiPolicy';

import StickyContainer from '../../components/StickyContainer';

import { StyledHeaderContainer, StyledHeaderPageContent, StyledPageContent, StyledTabsPanel } from './StyledComponents';
import ConnectedMaintenanceModule from '../../components/ConnectedMaintenanceModule';
import TenantPlacementUpsellBanner from '../../components/TenantPlacementUpsellBanner';
import RestrictedListingBanner from '../../components/RestrictedListingBanner';

import type { State as DashboardState } from './reducer';

type Location = {
  pathname: string;
  search: string;
};

type Props = {
  location?: Location;
  match?: {
    url: string;
  };
};

export const NAV_ITEM_TITLE = {
  DASHBOARD_APPLICATIONS: 'Applications',
  DASHBOARD_LEASES: 'Leases',
  DASHBOARD_LISTING: 'Listing',
  DASHBOARD_OVERVIEW: 'Overview',
  DASHBOARD_PAYMENTS: 'Payments',
  DASHBOARD_MAINTENANCE_TASKS: 'Maintenance',
};

const DASHBOARD_FETCH_TIMEOUT = 3000;

/**
 * Get a boolean that sets the value for the fluid prop for Page Content
 * on screen dimensions less than or equal to sm
 *
 * @param activePath string
 * @returns boolean
 */
const getFluidByActivePath = ({ activePath }: { activePath: string }) => {
  if (
    activePath === RMW_PATH.DASHBOARD_LISTING ||
    activePath === RMW_PATH.DASHBOARD_LEASES ||
    activePath === RMW_PATH.DASHBOARD_PAYMENTS
  ) {
    return true;
  }

  return false;
};

const getMarginBottomHeaderTabs = ({ constellationScreenSize }: { constellationScreenSize: string }) => {
  let value: string | number = 'lg';

  if (constellationScreenSize === 'lg' || constellationScreenSize === 'xl' || constellationScreenSize === 'xxl') {
    value = 5;
  }

  return value;
};

const DashboardPage = ({ location = { pathname: '', search: '' } }: Props) => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();

  const { dashboardState, reducerDispatch: dashboardReducerDispatch } = useContext(DashboardContext);
  const {
    // @ts-expect-error
    freemiumReducerDispatch,
  } = useContext(FreemiumContext);
  const { paths, isInMobileWebView } = useContext(RMWContext);

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const {
    active,
    constellationScreenSize,
    currentProperty,
    floorplans,
    isEnabled: isFreemiumEnabled,
    isRentalProtectionEligible,
    type: activeProductType,
    user,
    shouldShowRestrictionsBanner,
  } = useSelector((state) => {
    return {
      constellationScreenSize: state.app.constellationScreenSize,
      currentProperty: state.properties.currentProperty,
      active: state.properties.currentProperty.active,
      floorplans: state.properties.currentProperty.floorplans,
      isEnabled: state.properties.currentProperty.freemium.isEnabled,
      isRentalProtectionEligible: state.properties.currentProperty.rentalProtection.isEligible,
      type: state.properties.currentProperty.freemium.activeProduct.type,
      user: state.user,
      shouldShowRestrictionsBanner: state.properties.currentProperty.updateActiveStatus.showRestrictionOnDashboard,
    };
  }, shallowEqual);

  const { pathname } = location;

  const [activePath, setActivePath] = useState(
    `${RMW_SUB_PATH.DASHBOARD}/${stringHelper.getLastPieceFromPath(pathname)}`,
  );
  const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);

  // rental protection
  const [showSecurityDepositUpdateModal, setShowSecurityDepositUpdateModal] = useState(false);
  const [showSecurityDepositUpdateFailureModal, setShowSecurityDepositUpdateFailureModal] = useState(false);
  const [showReviewSecurityDepositModalOpen, setShowReviewSecurityDepositModalOpen] = useState(false);
  const [securityDepositUpdateModalProps, setSecurityDepositUpdateModalProps] = useState<{
    securityDeposit: string;
    rentAmount: string;
    selectedOption: string;
  } | null>(null);

  const featuresOfPremiumRef = useRef<HTMLDivElement>(null);
  const prevActivePath = useRef('');

  const navBarRef = useRef<HTMLDivElement>(null);

  const { error } = useGetPropertyDetailsOverview(listingAlias);

  const { data: restrictionsData } = useGetRestrictions(listingAlias);

  const { mutateAsync: updateSecurityDepositAndRentPrice } = useUpdateSecurityDepositAndRentPrice();
  const { data: enabledStates = '' } = useClientAppConfigWebApiHandler_getFeature({
    appName: 'zrm_web',
    featureName: 'tenantPlacementStates',
  });

  const {
    fluidSmallLTE: dashboardFluidSmallLTE = false,
    isFetching: isDashboardStateFetching,
  }: Partial<DashboardState> = dashboardState;

  const displayFeaturesOfPremium =
    isFreemiumEnabled &&
    activePath === RMW_PATH.DASHBOARD_LISTING &&
    (active || activeProductType === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM);

  const DASHBOARD_PATH = {
    applications: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_APPLICATIONS}`,
    leases: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_LEASES}`,
    payments: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_PAYMENTS}`,
    listing: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_LISTING}`,
    overview: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_OVERVIEW}`,
    maintenanceTasks: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_MAINTENANCE_TASKS}`,
  };

  const RMW_PATH_MAP = {
    [RMW_PATH.DASHBOARD_APPLICATIONS]: DASHBOARD_PATH.applications,
    [RMW_PATH.DASHBOARD_LEASES]: DASHBOARD_PATH.leases,
    [RMW_PATH.DASHBOARD_OVERVIEW]: DASHBOARD_PATH.overview,
    [RMW_PATH.DASHBOARD_LISTING]: DASHBOARD_PATH.listing,
    [RMW_PATH.DASHBOARD_PAYMENTS]: DASHBOARD_PATH.payments,
    [RMW_PATH.DASHBOARD_MAINTENANCE_TASKS]: DASHBOARD_PATH.maintenanceTasks,
  };

  const floorPlanId = floorplans[0]?.floorPlanId;

  // per design there is no margin bottom for the overview tab
  const marginBottomHeaderTabsDefaultValue =
    activePath !== RMW_PATH.DASHBOARD_OVERVIEW ? getMarginBottomHeaderTabs({ constellationScreenSize }) : 0;

  const shouldDisplayBanner = isRentalProtectionEligible && activePath === RMW_PATH.DASHBOARD_OVERVIEW;
  const pageBackgroundColorName = activePath === RMW_PATH.DASHBOARD_OVERVIEW ? 'coolGray100' : 'white';

  // @ts-expect-error
  const isLayoutHorizontal = !isInMobileWebView && globalThis.isLayoutHorizontal;

  const canAccessTenantPlacementDashboardBanner =
    !currentProperty.dateLastActivatedTimestamp &&
    accessUtils.canAccessTenantPlacement({
      enabledStates,
      currentProperty,
      user,
      cookie: TENANT_PLACEMENT_COOKIES.HAS_CLOSED_BANNER,
    });

  useEffect(() => {
    const fluidSmallLTE = getFluidByActivePath({ activePath });

    // update dashboard context
    // the child components will add conditionals to hide or show borders
    dashboardActions.updateAnyProperty({
      reducerDispatch: dashboardReducerDispatch,
      data: {
        fluidSmallLTE,
        activePath,
      },
    });
  }, [activePath]);

  useEffect(() => {
    if (restrictionsData) {
      syncCurrentPropertyRestrictions({ data: restrictionsData, dispatch });
    }
  }, [restrictionsData]);

  useEffect(() => {
    if (isDashboardStateFetching) {
      setTimeout(() => {
        dashboardActions.updateAnyProperty({
          reducerDispatch: dashboardReducerDispatch,
          data: {
            isFetching: false,
          },
        });
      }, DASHBOARD_FETCH_TIMEOUT);
    }
  }, [isDashboardStateFetching]);

  useEffect(() => {
    if (featuresOfPremiumRef) {
      setFeaturesOfPremiumRef();
    }
  }, [featuresOfPremiumRef]);

  useEffect(() => {
    prevActivePath.current = activePath;
    const lastPiece = stringHelper.getLastPieceFromPath(pathname) || '';
    const updatedActivePath = `${RMW_SUB_PATH.DASHBOARD}/${lastPiece}`;

    setActivePath(updatedActivePath);
    DashboardCSEvents.ViewDashboardTab({ user, tab: lastPiece });
  }, [pathname]);

  useEffect(() => {
    checkForRentalProtectionQueryParams();
  }, []);

  /**
   * sets a ref to the features of premium information
   * this is used in an outside component to scroll to the features of premium section
   */
  const setFeaturesOfPremiumRef = () => {
    freemiumActions.updateData({
      reducerDispatch: freemiumReducerDispatch,
      data: {
        featuresOfPremiumRef,
      },
    });
  };

  const checkForRentalProtectionQueryParams = () => {
    if (location?.search?.includes('rentalProtectionPolicyDropped=true')) {
      enqueueToast(<Toast appearance="success" body="You dropped out of Rental Protection request" />);
    }

    if (location?.search?.includes('updateDepositOnPolicyDrop')) {
      const { rentAmount, securityDeposit, selectedOption } = parseQueryString(location.search);

      setSecurityDepositUpdateModalProps({
        securityDeposit,
        rentAmount,
        selectedOption,
      });

      setShowSecurityDepositUpdateModal(true);
    }

    if (location?.search?.includes('reviewSecurityDeposit')) {
      setShowReviewSecurityDepositModalOpen(true);
    }
  };

  const closeSecurityDepositUpdateModal = () => {
    setShowSecurityDepositUpdateModal(false);
  };

  const closeReviewSecurityDepositModal = () => {
    setShowReviewSecurityDepositModalOpen(false);
  };

  const closeSecurityDepositUpdateFailureModal = () => {
    setShowSecurityDepositUpdateFailureModal(false);
  };

  const handleLoadComplete = useCallback(() => {
    setIsLoadingComplete(true);
  }, []);

  const handleSelected = useCallback(
    (id: string) => {
      const prevScrollPos = window.scrollY;
      let scrollPos;
      let navBarOffsetTop = navBarRef.current?.offsetTop;
      if (navBarOffsetTop) {
        navBarOffsetTop -= 16;
        scrollPos = prevScrollPos >= navBarOffsetTop ? navBarOffsetTop : prevScrollPos;
      }

      if (RMW_PATH_MAP[id]) {
        window.router.transitionTo(RMW_PATH_MAP[id], {}, scrollPos);
      }
    },
    [listingAlias],
  );

  const handleSecurityDepositUpdate = (
    updatedDeposit: string,
    updatedRentAmount: string,
    selectedPaymentOption: string,
  ) => {
    const updatePromise = selectedPaymentOption
      ? updateSecurityDepositAndRentPrice({
          listingAlias,
          securityDeposit: updatedDeposit,
          floorPlanId,
          rentPrice: updatedRentAmount,
        })
      : dispatch(
          PropertiesActions.updateSecurityDepositForListing(
            listingAlias,
            numberUtils.asCurrency(Number(updatedDeposit), '0.00'),
          ),
        );

    updatePromise
      // @ts-expect-error
      .then(() => {
        enqueueToast(<Toast appearance="success" body="You updated your security deposit" />);
        setShowSecurityDepositUpdateFailureModal(false);
      })
      .catch(() => {
        setShowSecurityDepositUpdateFailureModal(true);
        setShowSecurityDepositUpdateModal(false);
      });
  };

  const renderPageContent = () => {
    return (
      <Tabs.Panels>
        <StyledTabsPanel aria-labelledby={RMW_PATH.DASHBOARD_OVERVIEW}>
          <OverviewPage />
        </StyledTabsPanel>
        <StyledTabsPanel aria-labelledby={RMW_PATH.DASHBOARD_LISTING}>
          <ConnectedListing />
        </StyledTabsPanel>
        <StyledTabsPanel aria-labelledby={RMW_PATH.DASHBOARD_APPLICATIONS}>
          <ConnectedInterestedRenters />
        </StyledTabsPanel>
        <StyledTabsPanel aria-labelledby={RMW_PATH.DASHBOARD_LEASES}>
          <ConnectedLeaseAndPropertyConditionModule />
        </StyledTabsPanel>
        <StyledTabsPanel aria-labelledby={RMW_PATH.DASHBOARD_PAYMENTS}>
          <ConnectedPaymentsModule />
        </StyledTabsPanel>
        <StyledTabsPanel aria-labelledby={RMW_PATH.DASHBOARD_MAINTENANCE_TASKS}>
          <ConnectedMaintenanceModule />
        </StyledTabsPanel>
      </Tabs.Panels>
    );
  };

  return (
    <AuthCheckWrapper>
      <Page
        data-testid="DashboardPage"
        backgroundColor={pageBackgroundColorName}
        data-eventid={DashboardCSEvents.ViewDashboardTab.data_event_id}
        data-eventdescription="View dashboard tab event"
      >
        {error ? (
          <Box css={{ height: '100vh' }}>
            <PageBreadcrumb
              backButton={
                <TextButton asChild={true}>
                  <a href={paths.propertiesPage}>Your properties</a>
                </TextButton>
              }
            />
            <PageContent>
              <Paragraph>
                {/* @ts-expect-error */}
                {error?.error?.httpStatus === '403'
                  ? ErrorMessageConstants.LISTING_NO_PERMISSION
                  : ErrorMessageConstants.DEFAULT_LISTING_ERROR}
              </Paragraph>
            </PageContent>
          </Box>
        ) : (
          <>
            <Tabs selected={activePath} onSelected={handleSelected}>
              {isLoadingComplete && (
                <>
                  {shouldShowRestrictionsBanner && <RestrictedListingBanner isFromDashboard={true} />}
                  {!shouldShowRestrictionsBanner && canAccessTenantPlacementDashboardBanner && (
                    <TenantPlacementUpsellBanner />
                  )}
                </>
              )}

              <StyledHeaderContainer data-testid="DashboardHeader">
                <StyledHeaderPageContent>
                  <Spacer marginX="sm">
                    <PropertyCard listingAlias={listingAlias} onLoadComplete={handleLoadComplete} />
                  </Spacer>

                  {/* adding spacer here and within StickyContainer to account for margin when nav is sticky */}
                  <Spacer marginBottom="md" ref={navBarRef} />
                  <StickyContainer
                    backgroundColor="white"
                    borderBottomColor="borderLight"
                    passDownFixedStatus={true}
                    offsetForLeftNav={isLayoutHorizontal}
                    fixedOuterContainerStyles={{ height: 64, boxSizing: 'content-box' }}
                    isLoading={!isLoadingComplete}
                  >
                    <Spacer marginBottom="sm" />
                    <Navigation activePath={activePath} dashboardPathObj={DASHBOARD_PATH} />
                  </StickyContainer>
                </StyledHeaderPageContent>
              </StyledHeaderContainer>
              {isLoadingComplete && shouldDisplayBanner && (
                <div data-testid="BannerContainer">
                  <RentGuaranteeBannerMultiPolicy />
                </div>
              )}
              <Spacer
                data-testid="HeaderBottomMargin"
                marginBottom={{ default: marginBottomHeaderTabsDefaultValue, xl: 5 }}
              />
              <StyledPageContent
                data-testid="DashboardPageContent"
                fluidSmallLTE={dashboardFluidSmallLTE}
                marginBottom={{ lg: 5 }}
              >
                {renderPageContent()}
              </StyledPageContent>
            </Tabs>

            {/* need to set margin larger because component has an offset via StyledMobileDiv */}
            {isLoadingComplete && displayFeaturesOfPremium && (
              <Spacer marginTop={{ default: 'xl', xl: 5 }} ref={featuresOfPremiumRef}>
                <FeaturesOfPremium />
              </Spacer>
            )}

            {showSecurityDepositUpdateModal && (
              // @ts-expect-error
              <SecurityDepositUpdateOnRentGuaranteeDropModal
                close={closeSecurityDepositUpdateModal}
                handleSecurityDepositUpdate={handleSecurityDepositUpdate}
                listingAlias={listingAlias}
                {...securityDepositUpdateModalProps}
              />
            )}

            {showSecurityDepositUpdateFailureModal && (
              <SecurityDepositUpdateFailureModal close={closeSecurityDepositUpdateFailureModal} />
            )}

            {showReviewSecurityDepositModalOpen && (
              <ReviewSecurityDepositModal onClose={closeReviewSecurityDepositModal} listingAlias={listingAlias} />
            )}

            <InviteToApplyModal />
          </>
        )}
      </Page>
    </AuthCheckWrapper>
  );
};

export default withRouter(DashboardPage);
